import type { ProviderKind, ProviderPublicConfig } from '@pipi/shared';
import {
  Check,
  Cloud,
  Database,
  Disc3,
  FileMusic,
  HardDrive,
  KeyRound,
  Library,
  Link2,
  LoaderCircle,
  MonitorCog,
  Music2,
  Radio,
  RefreshCw,
  Save,
  Server,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { TvButton } from '../components/tv-button';
import { api } from '../lib/api';
import { useAppState } from '../state/app-state';

const providerIcons: Record<ProviderKind, typeof Cloud> = {
  demo: Sparkles,
  allkaraoke: Cloud,
  local: FileMusic,
  mugen: Radio,
  jellyfin: Library,
  plex: Disc3,
};

const fieldDefinitions: Partial<Record<ProviderKind, Array<{ key: string; label: string; placeholder: string; secret?: boolean }>>> = {
  local: [{ key: 'rootPath', label: '媒体目录', placeholder: '/Volumes/Music/Karaoke' }],
  mugen: [{ key: 'rootPath', label: 'Mugen 仓库目录', placeholder: '/path/to/KaraokeMugen/repos/your-repo' }],
  jellyfin: [
    { key: 'baseUrl', label: 'Jellyfin 地址', placeholder: 'http://192.168.1.10:8096' },
    { key: 'apiKey', label: 'API Key', placeholder: '在 Jellyfin 控制台创建', secret: true },
    { key: 'userId', label: '用户 ID（可选）', placeholder: '用于用户曲库权限' },
    { key: 'libraryId', label: '媒体库 ID（可选）', placeholder: '留空则扫描可见音乐库' },
  ],
  plex: [
    { key: 'baseUrl', label: 'Plex 地址', placeholder: 'http://192.168.1.10:32400' },
    { key: 'token', label: 'X-Plex-Token', placeholder: 'Plex 访问令牌', secret: true },
    { key: 'sectionId', label: '音乐库 ID（可选）', placeholder: '留空则扫描全部库' },
  ],
};

function statusLabel(provider: ProviderPublicConfig) {
  if (provider.status === 'syncing') return '同步中';
  if (provider.status === 'ready') return provider.enabled ? '已连接' : '已停用';
  if (provider.status === 'needs-setup') return '待配置';
  if (provider.status === 'offline') return '离线快照';
  return '需要处理';
}

function ProviderDialog({ provider, onClose }: { provider: ProviderPublicConfig; onClose: () => void }) {
  const { updateProvider, testProvider, syncProvider } = useAppState();
  const [form, setForm] = useState<Record<string, unknown>>(provider.config);
  const [working, setWorking] = useState<'save' | 'test' | 'sync' | null>(null);
  const fields = fieldDefinitions[provider.kind] ?? [];

  const save = async () => {
    setWorking('save');
    try {
      await updateProvider(provider.id, { enabled: true, config: form });
      onClose();
    } finally {
      setWorking(null);
    }
  };

  const test = async () => {
    setWorking('test');
    try {
      await updateProvider(provider.id, { enabled: true, config: form });
      await testProvider(provider.id);
    } finally {
      setWorking(null);
    }
  };

  const sync = async () => {
    setWorking('sync');
    try {
      await updateProvider(provider.id, { enabled: true, config: form });
      await syncProvider(provider.id);
      onClose();
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="provider-dialog" role="dialog" aria-modal="true" aria-labelledby="provider-title">
        <header>
          <div><span className="eyebrow">CONNECT LIBRARY</span><h2 id="provider-title">连接 {provider.name}</h2></div>
          <TvButton iconOnly tone="quiet" aria-label="关闭" onClick={onClose}><X /></TvButton>
        </header>
        {fields.length ? (
          <div className="provider-form">
            {fields.map((field) => (
              <label key={field.key}>
                <span>{field.label}</span>
                <input
                  data-tv-focusable
                  type={field.secret ? 'password' : 'text'}
                  value={String(form[field.key] ?? '')}
                  placeholder={field.placeholder}
                  onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.value }))}
                />
              </label>
            ))}
          </div>
        ) : (
          <div className="provider-explainer">
            <Cloud size={40} />
            <div><h3>无需账号即可同步</h3><p>只同步公开曲目索引、歌词时间轴和音高信息。视频仍由 YouTube 在线播放。</p></div>
          </div>
        )}
        {provider.error && <p className="dialog-error">{provider.error}</p>}
        <div className="provider-security"><ShieldCheck size={20} /><p>访问令牌只保存在你这台设备的本地服务中，不会下发给电视浏览器或上传云端。</p></div>
        <footer>
          <TvButton onClick={() => void test()} disabled={Boolean(working)}>
            {working === 'test' ? <LoaderCircle className="spin" /> : <Link2 />}测试连接
          </TvButton>
          <TvButton onClick={() => void sync()} disabled={Boolean(working)}>
            {working === 'sync' ? <LoaderCircle className="spin" /> : <RefreshCw />}保存并同步
          </TvButton>
          <TvButton tone="primary" onClick={() => void save()} disabled={Boolean(working)}>
            {working === 'save' ? <LoaderCircle className="spin" /> : <Save />}仅保存
          </TvButton>
        </footer>
      </section>
    </div>
  );
}

export function SettingsPage() {
  const { data, updateProvider, syncProvider, notify } = useAppState();
  const [editing, setEditing] = useState<ProviderPublicConfig | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [reduceMotion, setReduceMotion] = useState(() => localStorage.getItem('pipi:reduce-motion') === 'true');
  const [autoNext, setAutoNext] = useState(() => localStorage.getItem('pipi:auto-next') !== 'false');
  const [adminToken, setAdminToken] = useState(() => api.getAdminToken());
  const totalSongs = useMemo(() => data?.providers.reduce((total, provider) => total + provider.songCount, 0) ?? 0, [data]);
  if (!data) return null;

  const quickSync = async (provider: ProviderPublicConfig) => {
    setSyncing(provider.id);
    try {
      await syncProvider(provider.id);
    } finally {
      setSyncing(null);
    }
  };

  return (
    <div className="settings-page">
      <section className="settings-overview">
        <div><Database /><span><strong>{totalSongs.toLocaleString()}</strong><small>统一曲库歌曲</small></span></div>
        <div><Server /><span><strong>{data.providers.filter((provider) => provider.enabled).length}</strong><small>已启用来源</small></span></div>
        <div><HardDrive /><span><strong>{data.providers.filter((provider) => provider.enabled && provider.status === 'ready').length}</strong><small>当前可用</small></span></div>
      </section>

      <section className="settings-section">
        <header className="section-heading"><div><span>SONG PROVIDERS</span><h2>曲库来源</h2></div><p>媒体留在原处，皮皮只建立统一索引。</p></header>
        <div className="provider-grid">
          {data.providers.map((provider) => {
            const Icon = providerIcons[provider.kind];
            return (
              <article key={provider.id} className={`provider-card provider-card--${provider.status}`}>
                <div className="provider-card__icon"><Icon size={29} /></div>
                <div className="provider-card__copy"><span>{statusLabel(provider)}</span><h3>{provider.name}</h3><p>{provider.songCount.toLocaleString()} 首 · {provider.lastSyncAt ? `更新于 ${new Date(provider.lastSyncAt).toLocaleDateString('zh-CN')}` : '尚未同步'}</p></div>
                <button
                  type="button"
                  data-tv-focusable
                  role="switch"
                  aria-checked={provider.enabled}
                  className={provider.enabled ? 'switch is-on' : 'switch'}
                  disabled={provider.kind === 'demo'}
                  onClick={() => void updateProvider(provider.id, { enabled: !provider.enabled })}
                ><span /></button>
                <div className="provider-card__actions">
                  {provider.kind !== 'demo' && <TvButton tone="quiet" onClick={() => setEditing(provider)}><SlidersHorizontal size={19} />配置</TvButton>}
                  {provider.enabled && provider.kind !== 'demo' && (
                    <TvButton tone="quiet" onClick={() => void quickSync(provider)} disabled={syncing === provider.id}>
                      {syncing === provider.id ? <LoaderCircle className="spin" /> : <RefreshCw size={19} />}同步
                    </TvButton>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="settings-lower-grid">
        <article className="remote-setup-card">
          <div><span className="eyebrow">PHONE REMOTE</span><h2>手机点歌遥控</h2><p>同一局域网打开地址，输入 PIN 即可点歌和控制电视。</p></div>
          <div className="remote-setup-card__qr"><QRCodeSVG value={data.remote.remoteUrl} size={124} fgColor="#061525" bgColor="#fff9e9" /><strong>{data.remote.pin}</strong><span>房间 PIN</span></div>
        </article>
        <article className="experience-card">
          <header><MonitorCog /><div><span className="eyebrow">TV EXPERIENCE</span><h2>播放体验</h2></div></header>
          <label data-tv-focusable tabIndex={0}><span><strong>自动播放下一首</strong><small>结算后 8 秒自动准备队列下一首</small></span><input type="checkbox" checked={autoNext} onChange={(event) => { setAutoNext(event.target.checked); localStorage.setItem('pipi:auto-next', String(event.target.checked)); }} /></label>
          <label data-tv-focusable tabIndex={0}><span><strong>减少动态效果</strong><small>关闭卡片缩放和背景漂移</small></span><input type="checkbox" checked={reduceMotion} onChange={(event) => { setReduceMotion(event.target.checked); localStorage.setItem('pipi:reduce-motion', String(event.target.checked)); document.documentElement.dataset.reduceMotion = String(event.target.checked); }} /></label>
        </article>
        <article className="admin-access-card">
          <header><KeyRound /><div><span className="eyebrow">ADMIN ACCESS</span><h2>曲库管理密钥</h2></div></header>
          <p>公网版用管理密钥保护曲库地址、访问令牌和同步操作。浏览、点歌和手机遥控不受影响。</p>
          <div className="admin-access-card__form">
            <input
              data-tv-focusable
              type="password"
              value={adminToken}
              autoComplete="off"
              placeholder="粘贴部署时生成的管理密钥"
              onChange={(event) => setAdminToken(event.target.value)}
            />
            <TvButton
              tone="primary"
              onClick={() => {
                api.setAdminToken(adminToken);
                notify(adminToken.trim() ? '管理密钥已保存在这台设备' : '管理密钥已清除', 'success');
              }}
            ><Save />保存到本机</TvButton>
          </div>
        </article>
      </section>
      {editing && <ProviderDialog provider={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
